/**
 * Tally a message's reactions into one entry per emoji.
 *
 * The same loop was written out three times — twice in the group pane (stream and
 * thread) and once in the direct-message pane — each accumulating into an object
 * and then reassigning the same `let` to `Object.values(...)`, so the variable
 * changed type halfway through. One function, one return type.
 *
 * Several people can react to the same message, and to the same emoji, so an
 * entry also carries WHO reacted (`reactors`) and whether the viewer is one of
 * them (`mine`) — the pill needs both to show a count and to render as selected.
 *
 * @param {Array<{emoji_id: string|number, user_id?: string, reactor?: object}>} reactions
 *   raw `chat_reactions`
 * @param {string} [currentUserId] viewer, used to flag their own reaction
 * @returns {Array<object>} one entry per emoji, carrying `Count`, `reactors`, `mine`
 */
export const countReactions = (reactions, currentUserId) => {
  const byEmoji = new Map();

  for (const reaction of reactions || []) {
    const key = reaction?.emoji_id;
    if (key === undefined || key === null) continue;

    const seen = byEmoji.get(key);
    const reactors = seen ? [...seen.reactors] : [];
    if (reaction?.reactor || reaction?.user_id) {
      reactors.push(reaction.reactor ?? { id: reaction.user_id });
    }

    // Spread the latest reaction so the entry keeps that shape, as before.
    byEmoji.set(key, {
      ...reaction,
      Count: (seen?.Count ?? 0) + 1,
      reactors,
      mine:
        (seen?.mine ?? false) ||
        (currentUserId != null && String(reaction?.user_id) === String(currentUserId)),
    });
  }

  return Array.from(byEmoji.values());
};

/** Display name for a reactor, for the pill's hover title. */
export const reactorName = (reactor) =>
  [reactor?.nick_name || reactor?.first_name, reactor?.last_name].filter(Boolean).join(' ').trim();

/**
 * Fold an `emoji:receive` event into a message list.
 *
 * Written out three times before (stream, thread, direct), each with the same
 * defect: `message.chat_reactions?.findIndex(...)` returns `undefined` when the
 * message has no reactions yet, and `undefined !== -1` is true, so the "update
 * the existing one" branch ran with `updatedReactions[undefined] = …` — the
 * first reaction on a message produced an array with a stray property instead of
 * an entry, and the pill never appeared until a refetch.
 *
 * The server now sends `reactions`: the complete, authoritative set for the
 * message. Replacing wholesale is idempotent, so a duplicated or dropped event
 * cannot leave the pane permanently diverged. `chat_reactions` (one row, or
 * `emoji_id: null` for a removal) is the older single-delta shape and is still
 * honoured so this keeps working against a server that has not been deployed yet.
 *
 * @param {Array<object>} messages current list
 * @param {object} data socket payload
 * @returns {Array<object>} new list (same reference contents when nothing matched)
 */
export const applyReactionEvent = (messages, data) => {
  const { message_id: messageId, reactions, chat_reactions: delta } = data || {};
  if (!messageId) return messages || [];

  return (messages || []).map((message) => {
    if (message?.id !== messageId) return message;

    if (Array.isArray(reactions)) return { ...message, chat_reactions: reactions };
    if (!delta) return message;

    // One reaction per person: drop whatever they had, then re-add unless this
    // event is their removal. Covers add / change / remove in one path.
    const others = (message?.chat_reactions || []).filter(
      (reaction) => String(reaction?.user_id) !== String(delta?.user_id)
    );
    return {
      ...message,
      chat_reactions: delta?.emoji_id == null ? others : [...others, delta],
    };
  });
};

export default countReactions;
